/**
 * @module employee_attribute.transformer
 */
import { defaultLang as defaultLangSetting, supportedLanguages } from '~/config/settings/app.settings';
import { text } from '~/tools/text';

/**
 * Transform from an attribute value dictionary to an array of GraphQL objects
 * expected by the backend (e.g., for use with `getEmployeeDetails` query).
 *
 * @param {Object} attributeValues - a dictionary of attribute ids referencing an array of attribute values
 * @param {Array} excludedAttributeIds - an array of attribute ids to exclude from the output
 * @returns {Array} a list of Graphql-formatted objects for each attribute with its values
 *
 * @example input
 * {
 *   123: ['value1', 'value2'],
 *   456: ['value3']
 * }
 *
 * @example output
 * [
 *   {
 *     id: 123,
 *     type: 'STRING',
 *     values: ['value1', 'value2']
 *   },
 *   {
 *     id: 456,
 *     type: 'STRING',
 *     values: ['value3']
 *   }
 * ]
 */
const attributeValuesToGraphqlFormat = (attributeValues = {}, excludedAttributeIds = []) => {
  // @ts-expect-error TS(2769): No overload matches this call.
  const formattedAttributeValues = Object.keys(attributeValues).reduce((acc, key) => {
    const attributeId = Number(key);

    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    if (excludedAttributeIds.includes(attributeId)) return acc;

    const values =
      attributeValues && attributeValues[key] && Array.isArray(attributeValues[key]) ? attributeValues[key] : [];
    const attributeValue = {
      id: attributeId,
      type: 'STRING',
      values,
    };

    return [...acc, attributeValue];
  }, []);

  return formattedAttributeValues;
};

/**
 * Transform Employee Attribute "names" array to translations dictionary.
 *
 * @param {Object} obj - destructured param object
 * @param {Array} obj.names - a set of "name/lang" objects from the server
 * @returns {Object} a dictionary of translated strings
 *
 * @see `/src/tools/propTypes` for example translations shape.
 *
 * @example names (from server)
 * names: [
 *   {
 *     "name": "Gender",
 *     "language": {
 *       "id": "1",
 *       "langISOCode": "en-ca"
 *     }
 *   }
 * ]
 */
const employeeAttributeTranslationsFrom = ({ names = [] }) => {
  const translations = supportedLanguages.reduce((dict, lang) => {
    // @ts-expect-error TS(2339): Property 'language' does not exist on type 'never'... Remove this comment to see the full error message
    const nameObj = names.find((n) => n?.language?.langISOCode === lang);

    // A collection of all translated strings for the given `lang`.
    const translation = {
      // @ts-expect-error TS(2339): Property 'name' does not exist on type 'never'.
      name: nameObj?.name,
    };

    return {
      ...dict,
      [lang]: translation,
    };
  }, {});

  return translations;
};

/**
 * Transform translation objects into a SurveyType "names" array.
 *
 * @param {Object} translations - a dictionary of translated strings
 * @param {Object} languageCodeIds - a dictionary that maps ISO codes to database IDs
 * @returns {Array} an array of "name/lang" objects formatted for the server
 *
 * @see ./language.transformer.js
 */
const employeeAttributeTranslationsToNames = (translations = {}, languageCodeIds = {}) => {
  if (!translations) return [];

  const names = Object.keys(translations).map((lang) => ({
    name: translations[lang].name,
    language: {
      id: languageCodeIds[lang],
      langISOCode: lang,
    },
  }));

  return names;
};

/**
 * Create an "employee attribute" for the front-end.
 *
 * NOTE: Attribute belong to a specific company. They should be normalized into
 * the `company` entity such that there is a `company.employeeAttributes`
 * property which contains all these attribute ids.
 *
 * @example employee_attributes (from server)
 * "attributes": [
 *   {
 *     "id": 50,
 *     "isReportingAttribute": false,
 *     "isPrivate": false,
 *     "baseAttributeId": 1,
 *     "companyId": 2,
 *     "isRequired": false,
 *     "isUnique": false,
 *     "type": {
 *       "id": 1,
 *       "name": "STRING"
 *     },
 *     "attributeNames": [
 *       {
 *         "name": "First Name",
 *         "language": {
 *           "id": "1"
 *         }
 *       }
 *     ]
 *   }
 * ]
 */
const employeeAttributeFrom = (attr = {}, options = {}) => {
  // @ts-expect-error TS(2339): Property 'defaultLang' does not exist on type '{}'... Remove this comment to see the full error message
  const { defaultLang = defaultLangSetting } = options;

  const translations = employeeAttributeTranslationsFrom({
    // @ts-expect-error TS(2339): Property 'attributeNames' does not exist on type '... Remove this comment to see the full error message
    names: attr.attributeNames,
  });

  const defaultName =
    // @ts-expect-error TS(2339): Property 'name' does not exist on type '{}'.
    attr.name || translations[defaultLang]?.name || text.findFirstTranslationFor(translations, 'name');

  return {
    // @ts-expect-error TS(2339): Property 'id' does not exist on type '{}'.
    id: Number(attr.id),
    // @ts-expect-error TS(2339): Property 'baseAttributeId' does not exist on type ... Remove this comment to see the full error message
    baseAttributeId: Number(attr.baseAttributeId) || null,
    name: defaultName,
    // @ts-expect-error TS(2339): Property 'type' does not exist on type '{}'.
    type: attr?.type?.name,
    translations: translations,
    // @ts-expect-error TS(2339): Property 'isPrivate' does not exist on type '{}'.
    isPrivate: Boolean(attr.isPrivate),
    // @ts-expect-error TS(2339): Property 'isRequired' does not exist on type '{}'.
    isRequired: Boolean(attr.isRequired),
    // @ts-expect-error TS(2339): Property 'isUnique' does not exist on type '{}'.
    isUnique: Boolean(attr.isUnique),
    // @ts-expect-error TS(2339): Property 'isReportingAttribute' does not exist on ... Remove this comment to see the full error message
    isPrimary: Boolean(attr.isReportingAttribute),
    // @ts-expect-error TS(2339): Property 'isArchived' does not exist on ... Remove this comment to see the full error message
    isArchived: Boolean(attr.isArchived),
    // @ts-expect-error TS(2339): Property 'isPhone' does not exist on ... Remove this comment to see the full error message
    isPhone: Boolean(attr.isPhone),

    // DEPRECATED: use translations instead
    // @ts-expect-error TS(2339): Property 'attributeNames' does not exist on type '... Remove this comment to see the full error message
    names: attr.attributeNames.map((name) => ({
      languageId: name.language.id,
      lang: name.language.langISOCode,
      value: name.name,
    })),
  };
};

const employeeAttributesFrom = (employeeAttributes = [], options = {}) => {
  // @ts-expect-error TS(2339): Property 'defaultLang' does not exist on type '{}'... Remove this comment to see the full error message
  const { defaultLang = defaultLangSetting } = options;
  return employeeAttributes.map((attr) => employeeAttributeFrom(attr, { defaultLang }));
};

export {
  attributeValuesToGraphqlFormat,
  employeeAttributeFrom,
  employeeAttributesFrom,
  employeeAttributeTranslationsFrom,
  employeeAttributeTranslationsToNames,
};
